/**
 * IntellioAuthorityBanner (§5.2)
 *
 * Shows actor identity, role, school scope, and on-behalf-of context.
 * When on-behalf-of is active, renders a stronger visual treatment.
 *
 * @module intellio-v3/IntellioAuthorityBanner
 */
import React from 'react';
import type { IntellioAuthorityContext } from './types.js';

export interface IntellioAuthorityBannerProps {
  authority: IntellioAuthorityContext;
}

export const IntellioAuthorityBanner: React.FC<IntellioAuthorityBannerProps> = ({ authority }) => {
  const hasOnBehalfOf = Boolean(authority.onBehalfOfLabel);

  return (
    <div
      data-testid="intellio-authority-banner"
      className={[
        'rounded-xl border px-4 py-3 font-[Albert_Sans]',
        hasOnBehalfOf
          ? 'border-primary/30 bg-primary/5'
          : 'border-gray-200 bg-surface-light',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3">
        {/* Actor chip */}
        <span
          data-testid="intellio-actor-chip"
          className="inline-flex items-center gap-1.5 rounded-lg bg-jungle/10 px-2.5 py-1 text-xs font-semibold text-jungle"
        >
          <span className="h-2 w-2 rounded-full bg-jungle" />
          {authority.actorLabel}
        </span>

        {/* Role */}
        <span className="text-xs text-irongrey/70">{authority.actorRole}</span>

        {/* Separator */}
        <span className="text-gray-300" aria-hidden="true">|</span>

        {/* School scope */}
        <span
          data-testid="intellio-scope-chip"
          className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-xs text-irongrey"
        >
          {authority.schoolLabel}
          {authority.scopeLabel && (
            <span className="text-irongrey/50">&middot; {authority.scopeLabel}</span>
          )}
        </span>

        {/* Freshness */}
        {authority.scopeFreshnessLabel && (
          <span className="text-xs text-irongrey/50">{authority.scopeFreshnessLabel}</span>
        )}
      </div>

      {/* On behalf of – stronger visual treatment */}
      {hasOnBehalfOf && (
        <div className="mt-2 flex items-center gap-2">
          <span
            data-testid="intellio-on-behalf-chip"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary/15 px-3 py-1 text-xs font-semibold text-primary"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            On behalf of: {authority.onBehalfOfLabel}
          </span>
        </div>
      )}

      {/* Sensitivity note */}
      {authority.sensitivityLabel && (
        <p className="mt-1.5 text-xs text-strawberry/80">{authority.sensitivityLabel}</p>
      )}
    </div>
  );
};
